import * as React from 'react';
import {
  DataGrid,
  GridToolbar,
  useGridApiRef,
  GridPagination,
} from '@mui/x-data-grid';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useState, useContext, useRef, useCallback, useEffect } from 'react';
import useWindowDimensions from '../hooks/useWindowDimensions';
import CollapseContext from '../hooks/CollapseContext';
import { SelectActionsMenu } from './SelectActionsMenu';
import {
  loadUserSettings,
  updateUserInLocalStorage,
  syncUserSettingsToDB,
} from '../lib/settings';

import commonTranslations from '../languages/common.json';
import { LanguageContext } from '../context/LanguageContext';
import { useAuth } from '../hooks/AuthProvider';
import { useFontSize } from '../context/FontSizeContext';

import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import moment from 'moment';

const sanitizeFormFieldToken = (value, fallback = 'field') => {
  const sanitized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return sanitized || fallback;
};

const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === '[object Object]';

const normalizeBooleanLike = (value) => {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return undefined;
};

const normalizeColumnVisibilityModel = (value) => {
  if (!isPlainObject(value)) return {};

  return Object.entries(value).reduce((acc, [field, isVisible]) => {
    const normalizedVisibility = normalizeBooleanLike(isVisible);
    if (normalizedVisibility !== undefined) {
      acc[field] = normalizedVisibility;
    }
    return acc;
  }, {});
};

const normalizeColumnWidthModel = (value) => {
  if (!isPlainObject(value)) return {};

  return Object.entries(value).reduce((acc, [field, width]) => {
    if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
      acc[field] = width;
    }
    return acc;
  }, {});
};

const normalizeFilterModel = (value, fallback) => {
  if (!isPlainObject(value)) return { ...fallback };

  return {
    ...fallback,
    ...value,
    items: Array.isArray(value.items)
      ? value.items.filter((item) => isPlainObject(item))
      : fallback.items,
  };
};

const normalizeDensity = (value, fallback = 'standard') =>
  ['compact', 'standard', 'comfortable'].includes(value) ? value : fallback;

const normalizeStoredSortModel = (sortby, fallback) => {
  if (!isPlainObject(sortby) || typeof sortby.column !== 'string' || !sortby.column) {
    return fallback;
  }

  const normalizedSort =
    sortby.sort === 'desc' || sortby.order === 'desc' ? 'desc' : 'asc';

  return [
    {
      field: sortby.column,
      sort: normalizedSort,
    },
  ];
};

const DataGridAccessibleCheckbox = React.forwardRef(function DataGridAccessibleCheckbox(
  props,
  ref
) {
  const {
    fieldNamePrefix = 'data-grid',
    inputProps,
    slotProps,
    id,
    name,
    ...other
  } = props;
  const checkboxInstanceId = React.useId().replace(/:/g, '');
  const ariaLabel =
    inputProps?.['aria-label'] ??
    slotProps?.input?.['aria-label'] ??
    other['aria-label'];
  const checkboxSlug = sanitizeFormFieldToken(ariaLabel, 'selection-checkbox');
  const fieldPrefix = sanitizeFormFieldToken(fieldNamePrefix, 'data-grid');
  const checkboxId = id || `${fieldPrefix}-${checkboxSlug}-${checkboxInstanceId}`;
  const checkboxName = name || `${fieldPrefix}-${checkboxSlug}`;
  const mergedInputProps = {
    ...(slotProps?.input || {}),
    ...(inputProps || {}),
    id: checkboxId,
    name: checkboxName,
  };

  return (
    <Checkbox
      {...other}
      ref={ref}
      id={checkboxId}
      name={checkboxName}
      inputProps={mergedInputProps}
      slotProps={{
        ...slotProps,
        input: mergedInputProps,
      }}
    />
  );
});

const panelGlobalStyles = {
  '.MuiDataGrid-panel': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },

  '.MuiDataGrid-panelWrapper': {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    width: '520px',
    maxWidth: '90vw',
    maxHeight: 'calc(100vh - 120px)',
    padding: '18px 22px 18px 22px',
    background: 'var(--dg-panel-bg)',
    border: '1px solid var(--dg-panel-border)',
    boxShadow: 'var(--dg-panel-shadow)',
    color: 'var(--dg-panel-text)',
  },

  '.MuiDataGrid-panelHeader': {
    padding: '4px 4px 12px 4px',
    marginBottom: '10px',
    borderBottom: '1px solid var(--dg-panel-divider)',
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--dg-panel-text)',
  },

  '.MuiDataGrid-panelContent': {
    padding: 0,
    margin: 0,
    flex: 1,
    overflowY: 'auto',
  },

  '.MuiDataGrid-panelFooter': {
    paddingTop: '10px',
    marginTop: '10px',
    borderTop: '1px solid var(--dg-panel-divider)',
    fontSize: '12px',
    color: 'var(--dg-panel-text-muted)',
  },

  '.MuiDataGrid-panelWrapper .MuiFormControl-root': {
    marginBottom: '10px',
  },
  '.MuiDataGrid-panelWrapper .MuiInputBase-root': {
    backgroundColor: 'transparent',
    borderRadius: '999px',
    border: '1px solid var(--dg-panel-input-border)',
  },
  '.MuiDataGrid-panelWrapper .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--dg-panel-input-outline)',
  },
  '.MuiDataGrid-panelWrapper .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
    {
      borderColor: 'var(--dg-panel-input-focus)',
    },
  '.MuiDataGrid-panelWrapper .MuiInputBase-input': {
    color: 'var(--dg-panel-text)',
    fontSize: '13px',
  },
  '.MuiDataGrid-panelWrapper .MuiInputLabel-root': {
    color: 'var(--dg-panel-label)',
    fontSize: '12px',
  },

  '.MuiDataGrid-panelWrapper .MuiDataGrid-columnsPanelRow': {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 4px',
    borderBottom: '1px solid var(--dg-panel-row-divider)',
    fontSize: '13px',
    color: 'var(--dg-panel-text)',
  },
  '.MuiDataGrid-panelWrapper .MuiDataGrid-columnsPanelRow:last-of-type': {
    borderBottom: 'none',
  },
  '.MuiDataGrid-panelWrapper .MuiDataGrid-columnsPanelRow:hover': {
    backgroundColor: 'var(--dg-panel-row-hover)',
  },

  '.MuiDataGrid-panelWrapper .MuiCheckbox-root, .MuiDataGrid-panelWrapper .MuiSvgIcon-root':
    {
      color: 'var(--dg-panel-accent)',
    },

  '.MuiDataGrid-panelWrapper .MuiFormControlLabel-label': {
    fontSize: '12px',
    color: 'var(--dg-panel-text)',
  },

  '.MuiDataGrid-panelWrapper .MuiDataGrid-filterForm': {
    backgroundColor: 'transparent',
    borderRadius: '12px',
    border: '1px solid var(--dg-panel-filter-border)',
    padding: '10px 12px',
    marginBottom: '10px',
  },

  '.MuiMenu-paper': {
    boxSizing: 'border-box',
    background: 'var(--dg-menu-bg)',
    borderRadius: '14px',
    border: '1px solid var(--dg-menu-border)',
    boxShadow: 'var(--dg-menu-shadow)',
    color: 'var(--dg-menu-text)',
    overflow: 'hidden',
    maxHeight: 'calc(100vh - 140px)',
  },

  '.MuiMenu-paper .MuiMenu-list': {
    paddingTop: 4,
    paddingBottom: 4,
    background: 'var(--dg-menu-bg)',
  },

  '.muiltr-1tktgsa-MuiPaper-root-MuiPopover-paper-MuiMenu-paper': {
    border: '1px solid',
  },
  '.MuiMenu-paper .MuiMenuItem-root': {
    fontSize: '13px',
    color: 'var(--dg-menu-text)',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 18,
    paddingRight: 18,
    borderBottom: '1px solid var(--dg-menu-divider)',
    border: '1px solid',
  },
  '.MuiMenu-paper .MuiMenuItem-root:last-of-type': {
    borderBottom: 'none',
  },
  '.MuiMenu-paper .MuiMenuItem-root:hover': {
    backgroundColor: 'var(--dg-menu-hover)',
  },
  '.MuiMenu-paper .MuiMenuItem-root.Mui-selected': {
    background: 'var(--dg-menu-selected-bg)',
    color: 'var(--dg-menu-selected-text)',
  },
  '.MuiMenu-paper .MuiMenuItem-root.Mui-selected:hover': {
    background: 'var(--dg-menu-selected-bg-hover)',
  },
  '.MuiMenu-paper .MuiMenuItem-root.Mui-disabled': {
    color: 'var(--dg-menu-disabled)',
  },
};

export default function DataTable({
  rows,
  columns,
  idcolumn,
  pagingoptions = [10, 20, 50, 100],
  sortField = 'id',
  sortType = 'asc',
  rowModesModel = {},
  handleRowModesModelChange = () => {},
  handleRowEditStop = () => {},
  processRowUpdate = (row) => row,
  model,
  handleRowClick = () => {},
  checkboxSelection = false,
  showToolbar = true,
  showHeader = true,
  rowSelectionModel = [],
  setRowSelectionModel = () => {},
  selectActions = [],
  size,
  isDetail = false,
  tableKey,
  pagination,
  getRowClassName,
  toolbarPrefix = null,
  toolbarSuffix = null,
  loading = false,
}) {
  const dims = useWindowDimensions();
  const apiRef = useGridApiRef();
  const gridRootRef = useRef(null);
  const { lang } = useContext(LanguageContext);
  useFontSize();
  const t = commonTranslations[lang].common.DataGridTable;
  const lastSavedSettingsRef = useRef(null);
  const toolbarInstanceId = React.useId().replace(/:/g, '');
  const sanitizedTableKey = React.useMemo(
    () => sanitizeFormFieldToken(tableKey, 'data-grid'),
    [tableKey]
  );
  const searchFieldId = `${sanitizedTableKey}-search-input-${toolbarInstanceId}`;
  const searchFieldName = `${sanitizedTableKey}-search`;
  const searchColumnLabelId = `${sanitizedTableKey}-search-column-label-${toolbarInstanceId}`;
  const searchColumnSelectId = `${sanitizedTableKey}-search-column-select-${toolbarInstanceId}`;
  const searchColumnName = `${sanitizedTableKey}-search-column`;

  const searchableColumns = React.useMemo(() => {
    return columns.filter(
      (column) =>
        column.field !== 'icon' &&
        column.field !== 'actions' &&
        column.type !== 'dateTime' &&
        column.type !== 'date'
    );
  }, [columns]);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchColumn, setSearchColumn] = useState(
    searchableColumns.length > 0 ? searchableColumns[0].field : ''
  );
  const [dateColumnFields, setDateColumnFields] = useState([]);
  const [activeCustomSearchFilters, setActiveCustomSearchFilters] = useState([]);
  const open = useContext(CollapseContext);
  const maxWidth = open ? `${dims.width - 90}px` : `${dims.width - 270}px`;
  const { user, setUser } = useAuth();
  const authenticatedUserRef = useRef(user);
  authenticatedUserRef.current = user;

  const headerStyle = {
    color: 'var(--devices-grid-header-text)',
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '0.02em',
  };

  const rowStyle = {
    color: 'var(--devices-grid-cell-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  };

  
  useEffect(() => {
    const root = gridRootRef.current;
    if (!root) return;

    const isEventFromThisGrid = (eventTarget) => {
      try {
        return !!(eventTarget && root.contains(eventTarget));
      } catch (e) {
        return false;
      }
    };

    const closeAnyMuiMenu = () => {
      const menuPaper =
        document.querySelector('.MuiMenu-paper') ||
        document.querySelector('[role="menu"]') ||
        document.querySelector('.MuiPopover-paper');
      if (!menuPaper) return;

      try {
        document.body.dispatchEvent(
          new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window })
        );
        document.body.dispatchEvent(
          new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window })
        );
      } catch (e) {}

      try {
        document.body.dispatchEvent(new Event('touchstart', { bubbles: true, cancelable: true }));
      } catch (e) {}

      try {
        window.dispatchEvent(
          new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true })
        );
      } catch (e) {}
    };

    const onWheelCapture = (e) => {
      if (!isEventFromThisGrid(e.target)) return;
      closeAnyMuiMenu();
    };

    const onTouchMoveCapture = (e) => {
      if (!isEventFromThisGrid(e.target)) return;
      closeAnyMuiMenu();
    };

    const onScrollCapture = (e) => {
      if (!isEventFromThisGrid(e.target)) return;
      closeAnyMuiMenu();
    };

    window.addEventListener('wheel', onWheelCapture, { passive: true, capture: true });
    window.addEventListener('touchmove', onTouchMoveCapture, { passive: true, capture: true });
    window.addEventListener('scroll', onScrollCapture, { passive: true, capture: true });
    window.addEventListener('resize', closeAnyMuiMenu);

    return () => {
      window.removeEventListener('wheel', onWheelCapture, true);
      window.removeEventListener('touchmove', onTouchMoveCapture, true);
      window.removeEventListener('scroll', onScrollCapture, true);
      window.removeEventListener('resize', closeAnyMuiMenu);
    };
  }, []);

useEffect(() => {
    const dateFields = columns
      .filter((col) => col.type === 'dateTime' || col.type === 'date')
      .map((col) => col.field);
    setDateColumnFields(dateFields);
  }, [columns]);

  useEffect(() => {
    if (
      searchableColumns.length > 0 &&
      (!searchableColumns.some((col) => col.field === searchColumn) ||
        searchColumn === '')
    ) {
      setSearchColumn(searchableColumns[0].field);
    } else if (searchableColumns.length === 0 && searchColumn !== '') {
      setSearchColumn('');
    }
  }, [searchableColumns, searchColumn]);

  function CustomPagination() {
    return (
      <GridPagination
        sx={{
          color: 'var(--devices-grid-pagination-text)',
          fontSize: 'calc(var(--ui-font-size) * var(--font-size-multiplier))',
        }}
      />
    );
  }

  const getDateRangeFilterItems = useCallback((field, term) => {
    if (!term) return [];

    let parsedMoment;

    const datetimeFormatsWithTime = [
      'DD/MM/YYYY HH:mm:ss',
      'DD/MM/YYYY HH:mm',
      'DD/MM/YYYY HH',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD HH',
    ];

    if (term.includes(':') || term.includes('T')) {
      parsedMoment = moment(
        term,
        datetimeFormatsWithTime.concat(moment.ISO_8601),
        true
      );
      if (
        parsedMoment.isValid() &&
        (parsedMoment.hours() !== 0 ||
          parsedMoment.minutes() !== 0 ||
          parsedMoment.seconds() !== 0 ||
          parsedMoment.milliseconds() !== 0)
      ) {
        return [{ field, operator: 'is', value: parsedMoment.toISOString() }];
      }
    }

    const dateOnlyFormats = [
      'YYYY-MM-DD',
      'MM/DD/YYYY',
      'DD/MM/YYYY',
      'MM-DD-YYYY',
      'DD-MM-YYYY',
    ];
    parsedMoment = moment(term, dateOnlyFormats, true);
    if (parsedMoment.isValid()) {
      const startDate = parsedMoment.startOf('day');
      const endDate = parsedMoment.endOf('day');
      return [
        { field, operator: 'onOrAfter', value: startDate.toISOString() },
        { field, operator: 'onOrBefore', value: endDate.toISOString() },
      ];
    }

    const monthYearOnlyFormats = ['YYYY-MM', 'MM/YYYY', 'M/YYYY'];
    parsedMoment = moment(term, monthYearOnlyFormats, true);
    if (parsedMoment.isValid()) {
      const startDate = parsedMoment.startOf('month');
      const endDate = parsedMoment.endOf('month');
      return [
        { field, operator: 'onOrAfter', value: startDate.toISOString() },
        { field, operator: 'onOrBefore', value: endDate.toISOString() },
      ];
    }

    const yearOnlyFormats = ['YYYY'];
    parsedMoment = moment(term, yearOnlyFormats, true);
    if (parsedMoment.isValid()) {
      const startDate = parsedMoment.startOf('year');
      const endDate = parsedMoment.endOf('year');
      return [
        { field, operator: 'onOrAfter', value: startDate.toISOString() },
        { field, operator: 'onOrBefore', value: endDate.toISOString() },
      ];
    }

    return [];
  }, []);

  const getInitialGridSettings = useCallback(() => {
    const defaultSettings = {
      sortModel: [{ field: sortField, sort: sortType }],
      columnVisibilityModel: {},
      columnWidthModel: {},
      filterModel: { items: [] },
      density: 'standard',
    };

    if (!tableKey) return { ...defaultSettings, _initialCustomSearchFilters: [] };

    const settingsUser =
      authenticatedUserRef.current?.id === user?.id
        ? authenticatedUserRef.current
        : null;
    const rawStoredSettings = loadUserSettings(tableKey, settingsUser);
    const stored = isPlainObject(rawStoredSettings) ? rawStoredSettings : {};

    const initialSortModel = normalizeStoredSortModel(
      stored.sortby,
      defaultSettings.sortModel
    );

    const loadedFilterModel = normalizeFilterModel(
      stored.filter,
      defaultSettings.filterModel
    );

    const initialCustomSearchFilters = (loadedFilterModel.items || []).filter(
      (item) =>
        typeof item.id === 'string' && item.id.startsWith('custom-search-filter')
    );
    const initialToolbarFilters = (loadedFilterModel.items || []).filter(
      (item) =>
        !(
          typeof item.id === 'string' &&
          item.id.startsWith('custom-search-filter')
        )
    );

    return {
      sortModel: initialSortModel,
      columnVisibilityModel: normalizeColumnVisibilityModel(stored.columns),
      columnWidthModel: normalizeColumnWidthModel(stored.columnWidths),
      filterModel: { ...loadedFilterModel, items: initialToolbarFilters },
      density: normalizeDensity(stored.density, defaultSettings.density),
      _initialCustomSearchFilters: initialCustomSearchFilters,
    };
  }, [tableKey, sortField, sortType, user?.id]);

  const [gridSettings, setGridSettings] = useState(getInitialGridSettings);
  const isInitialMount = useRef(true);

  useEffect(() => {
    setActiveCustomSearchFilters(
      gridSettings._initialCustomSearchFilters || []
    );
  }, [gridSettings._initialCustomSearchFilters]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const newInitialSettings = getInitialGridSettings();
    setGridSettings(newInitialSettings);
    setActiveCustomSearchFilters(
      newInitialSettings._initialCustomSearchFilters || []
    );
  }, [tableKey, getInitialGridSettings]);

  useEffect(() => {
    const generatedSearchFilters = [];

    if (searchTerm && searchColumn) {
      const selectedColumn = columns.find((col) => col.field === searchColumn);

      if (selectedColumn) {
        if (selectedColumn.type === 'dateTime' || selectedColumn.type === 'date') {
          const dateFilters = getDateRangeFilterItems(searchColumn, searchTerm);
          generatedSearchFilters.push(
            ...dateFilters.map((f, idx) => ({
              ...f,
              id: `custom-search-filter-date-${searchColumn}-${idx}`,
            }))
          );
        } else if (selectedColumn.type === 'number') {
          const numericValue = Number(searchTerm);
          if (!isNaN(numericValue)) {
            generatedSearchFilters.push({
              id: `custom-search-filter-number-${searchColumn}`,
              field: searchColumn,
              operator: '=',
              value: numericValue,
            });
          }
        } else {
          generatedSearchFilters.push({
            id: `custom-search-filter-text-${searchColumn}`,
            field: searchColumn,
            operator: 'contains',
            value: searchTerm,
          });
        }
      }
    }

    if (
      JSON.stringify(activeCustomSearchFilters) !==
      JSON.stringify(generatedSearchFilters)
    ) {
      setActiveCustomSearchFilters(generatedSearchFilters);
    }
  }, [
    searchTerm,
    searchColumn,
    columns,
    getDateRangeFilterItems,
    activeCustomSearchFilters,
  ]);

  const isServer = !!pagination?.serverSide;
  const isCheckboxSelectionEnabled = Boolean(checkboxSelection);
  const serverPage = pagination?.page ?? 1;
  const serverPageSize = pagination?.pageSize ?? (pagingoptions?.[0] ?? 10);
  const serverRowCount = pagination?.count ?? rows.length;
  const displayedTotalRows = isServer ? serverRowCount : (rows?.length ?? 0);

  useEffect(() => {
    setGridSettings((prev) => {
      const toolbarFilters = (prev.filterModel.items || []).filter(
        (item) =>
          !(
            typeof item.id === 'string' &&
            item.id.startsWith('custom-search-filter')
          )
      );

      const combinedItems = [...toolbarFilters, ...activeCustomSearchFilters];
      const newFilterModel = { ...prev.filterModel, items: combinedItems };

      if (JSON.stringify(prev.filterModel) === JSON.stringify(newFilterModel))
        return prev;
      return { ...prev, filterModel: newFilterModel };
    });
  }, [activeCustomSearchFilters]);

  // Notify server-side consumers after the controlled grid state has committed.
  // Calling a parent state setter from inside the setGridSettings updater can make
  // MUI re-emit onFilterModelChange while React is still processing that update,
  // which produces a maximum-update-depth loop when users type quickly.
  const lastEmittedServerFilterRef = useRef('');
  useEffect(() => {
    if (!isServer || typeof pagination?.onFilterModelChange !== 'function') return;

    const serializedFilterModel = JSON.stringify(
      gridSettings.filterModel || { items: [] }
    );
    if (lastEmittedServerFilterRef.current === serializedFilterModel) return;

    lastEmittedServerFilterRef.current = serializedFilterModel;
    pagination.onFilterModelChange(gridSettings.filterModel || { items: [] });
  }, [gridSettings.filterModel, isServer, pagination]);

  // Server-side pages own the request state. Hydrate that state from the
  // Users.settings JSON when the grid mounts (or the signed-in user/table
  // changes), otherwise the page-level defaults mask the stored preferences.
  const hydratedServerSettingsRef = useRef('');
  useEffect(() => {
    if (!isServer || !tableKey) return;

    const hydrationKey = `${user?.id || ''}:${tableKey}`;
    if (hydratedServerSettingsRef.current === hydrationKey) return;
    hydratedServerSettingsRef.current = hydrationKey;

    if (typeof pagination?.onSortModelChange === 'function') {
      pagination.onSortModelChange(gridSettings.sortModel || []);
    }
  }, [
    isServer,
    tableKey,
    user?.id,
    gridSettings.sortModel,
    pagination,
  ]);

  const handleGridFilterModelChange = useCallback(
    (newModelFromDataGrid) => {
      setGridSettings((prev) => {
        const toolbarFilters = (newModelFromDataGrid.items || []).filter(
          (item) =>
            !(
              typeof item.id === 'string' &&
              item.id.startsWith('custom-search-filter')
            )
        );

        const combinedItems = [...toolbarFilters, ...activeCustomSearchFilters];
        const newFilterModel = { ...newModelFromDataGrid, items: combinedItems };

        if (JSON.stringify(prev.filterModel) === JSON.stringify(newFilterModel))
          return prev;
        return { ...prev, filterModel: newFilterModel };
      });
    },
    [activeCustomSearchFilters]
  );

  const handleSearchTermChange = (event) => setSearchTerm(event.target.value);
  const handleSearchColumnChange = (event) => setSearchColumn(event.target.value);

  const isDateSearchColumn = dateColumnFields.includes(searchColumn);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!tableKey) return;

    const currentSortModel = gridSettings.sortModel?.[0];
    const settingsToSave = {
      sortby:
        currentSortModel && currentSortModel.field
          ? { column: currentSortModel.field, order: currentSortModel.sort }
          : null,
      columns: gridSettings.columnVisibilityModel,
      columnWidths: gridSettings.columnWidthModel,
      filter: gridSettings.filterModel,
      density: gridSettings.density,
    };

    const prevSettings = lastSavedSettingsRef.current;
    if (JSON.stringify(prevSettings) === JSON.stringify(settingsToSave)) return;

    lastSavedSettingsRef.current = settingsToSave;

    setUser((currentUser) => {
      if (!currentUser || Object.keys(currentUser).length === 0) return currentUser;
      const updatedUser = JSON.parse(JSON.stringify(currentUser));
      updatedUser.settings = updatedUser.settings || {};
      updatedUser.settings[tableKey] = settingsToSave;

      updateUserInLocalStorage(updatedUser);
      return updatedUser;
    });

    syncUserSettingsToDB(tableKey, settingsToSave);
  }, [
    gridSettings.sortModel,
    gridSettings.columnVisibilityModel,
    gridSettings.columnWidthModel,
    gridSettings.filterModel,
    gridSettings.density,
    tableKey,
    setUser,
  ]);

  const localeText = {
    toolbarColumns: t.columns,
    toolbarFilters: t.filter,
    toolbarDensity: t.density,
    toolbarDensityCompact: t.compact,
    toolbarDensityStandard: t.standard,
    toolbarDensityComfortable: t.comfortable,
    toolbarExport: t.export,
    toolbarExportCSV: t.exportCsv,
    toolbarExportPrint: t.print,
    MuiTablePagination: {
      labelRowsPerPage: t.rowsPerPage,
      labelDisplayedRows: ({ from, to, count }) =>
        t.displayedRows
          .replace('{from}', from)
          .replace('{to}', to)
          .replace('{count}', count),
    },
    filterOperatorContains: t.contains,
    filterOperatorEquals: t.equals,
    filterOperatorStartsWith: t.startsWith,
    filterOperatorEndsWith: t.endsWith,
    filterOperatorIsEmpty: t.isEmpty,
    filterOperatorIsNotEmpty: t.isNotEmpty,
    filterOperatorIsAnyOf: t.isAnyOf,
    filterPanelColumns: t.columns,
    filterPanelOperator: t.operators,
    filterPanelInputLabel: t.valueLabel,
    filterPanelInputPlaceholder: t.valuePlaceholder,
    columnMenuLabelColumns: t.columns,
    columnMenuFilter: t.filter,
    columnMenuDensity: t.density,
    columnMenuDensityCompact: t.compact,
    columnMenuDensityStandard: t.standard,
    columnMenuDensityComfortable: t.comfortable,
  };

  
const columnsWithWidths = React.useMemo(() => {
  const widthModel = gridSettings?.columnWidthModel || {};
  return (columns || []).map((col) => {
    const w = widthModel[col.field];
    if (w == null) return col;
    return { ...col, width: w };
  });
}, [columns, gridSettings?.columnWidthModel]);

  const effectiveSortModel = gridSettings.sortModel;

  const handleSortModelChange = (newModel) => {
    setGridSettings((prev) => ({ ...prev, sortModel: newModel }));
    if (isServer && pagination?.onSortModelChange) {
      pagination.onSortModelChange(newModel);
    }
  };

  const handlePaginationModelChange = (model) => {
    if (!isServer) return;
    const newPage = model.page + 1;
    const newPageSize = model.pageSize;

    if (pagination.onPageChange && newPage !== serverPage) pagination.onPageChange(newPage);
    if (pagination.onPageSizeChange && newPageSize !== serverPageSize)
      pagination.onPageSizeChange(newPageSize);
  };

  return (
    <>
      <GlobalStyles styles={panelGlobalStyles} />

      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {showToolbar && (
          <div style={{ height: 'auto', flexShrink: 0, marginTop: '6px' }}>
            <Stack
              spacing={1}
              direction="row"
              alignItems="center"
              sx={{ mb: 1, px: 0.5, gap: 1, alignContent: 'center', flexWrap: 'wrap' }}
              useFlexGap
            >
              {toolbarPrefix ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    minWidth: 0,
                  }}
                >
                  {toolbarPrefix}
                </Box>
              ) : null}

              {columns.length > 0 && (
                <>
                  <TextField
                    id={searchFieldId}
                    name={searchFieldName}
                    placeholder="Type here..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchTermChange}
                    type={isDateSearchColumn ? 'date' : 'text'}
                    InputLabelProps={isDateSearchColumn ? { shrink: true } : {}}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ fontSize: 18, color: 'var(--devices-search-icon)' }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      flexGrow: 1,
                      minWidth: '180px',
                      maxWidth: '340px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '999px',
                        border: '1px solid var(--devices-toolbar-btn-hover-border)',
                        '& fieldset': { borderColor: 'var(--devices-search-border)' },
                        '&:hover fieldset': {
                          borderColor: 'var(--devices-toolbar-btn-hover-border)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'var(--devices-toolbar-btn-hover-border)',
                          boxShadow:
                            '0 0 0 1px var(--devices-toolbar-btn-hover-ring)',
                        },
                      },
                      '& .MuiInputBase-input': {
                        fontSize:
                          'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                        color: 'var(--devices-search-input)',
                        paddingY: '7px',
                      },
                      '& .MuiInputLabel-root': { display: 'none' },
                    }}
                  />

                  <FormControl
                    sx={{
                      minWidth: '150px',
                      maxWidth: '220px',
                      '& .MuiInputBase-root': {
                        fontSize:
                          'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                        color: 'var(--devices-grid-cell-text)',
                        borderRadius: '999px',
                        border: '1px solid var(--devices-toolbar-btn-hover-border)',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize:
                          'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                        color: 'var(--dg-toolbar-label)',
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'var(--devices-search-border)' },
                        '&:hover fieldset': {
                          borderColor: 'var(--devices-toolbar-btn-hover-border)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'var(--devices-toolbar-btn-hover-border)',
                        },
                      },
                      '& .MuiSelect-icon': { color: 'var(--dg-toolbar-label)' },
                    }}
                    size="small"
                  >
                    <Select
                      labelId={searchColumnLabelId}
                      id={searchColumnSelectId}
                      name={searchColumnName}
                      value={searchColumn}
                      label={t.searchColumn}
                      onChange={handleSearchColumnChange}
                    >
                      {searchableColumns.map((column) => (
                        <MenuItem
                          key={column.field}
                          value={column.field}
                          sx={{
                            fontSize:
                              'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                            color: 'var(--root-color)',
                          }}
                        >
                          {column.headerName || column.field}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {selectActions &&
                selectActions.length > 0 &&
                rowSelectionModel &&
                rowSelectionModel.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      whiteSpace: 'nowrap',
                      background: 'var(--dg-actions-pill-bg)',
                      borderRadius: '999px',
                      px: 1,
                    }}
                  >
                    <SelectActionsMenu actions={selectActions} params={rowSelectionModel} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      whiteSpace: 'nowrap',
                      px: 1,
                      py: 0.25,
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize:
                          'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                        color: 'var(--dg-total-rows-text)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t.totalRows.replace('{count}', displayedTotalRows)}
                    </h4>
                  </Box>
                )}
              </Box>

              {toolbarSuffix ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    minWidth: 0,
                  }}
                >
                  {toolbarSuffix}
                </Box>
              ) : null}
            </Stack>
          </div>
        )}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            height: '100%',
            borderRadius: 'var(--dg-wrapper-radius)',
            padding: 'var(--dg-wrapper-padding)',
            background: 'var(--dg-wrapper-bg)',
            boxShadow: 'var(--dg-wrapper-shadow)',
            border: '1px solid var(--dg-wrapper-border)',
          }}
          ref={gridRootRef}
          className="devices-grid-wrapper"
        >
          <DataGrid
            apiRef={apiRef}
            rows={rows}
            columns={columnsWithWidths}
            loading={loading}
            getRowId={(row) => row[idcolumn]}
            pageSizeOptions={pagingoptions}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 100 } },
            }}
            checkboxSelection={isCheckboxSelectionEnabled}
            onRowSelectionModelChange={(selected) => setRowSelectionModel(selected)}
            rowSelectionModel={rowSelectionModel}
            disableRowSelectionOnClick
            sx={{
              height: '100%',
              width: '100%',
              border: 0,
              backgroundColor: 'transparent',
              color: 'var(--cell-color)',
              fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',

              '& .MuiDataGrid-container--top [role="row"], & .MuiDataGrid-container--bottom [role="row"]':
                {
                  backgroundColor: 'transparent',
                },

              '& .MuiDataGrid-columnHeader': {
                color: headerStyle.color,
                fontWeight: headerStyle.fontWeight,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: headerStyle.fontSize,
                letterSpacing: headerStyle.letterSpacing,
              },

              '& .MuiDataGrid-cell': {
                ...rowStyle,
                borderBottom: '1px solid var(--devices-grid-cell-border-strong)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                background: 'var(--devices-grid-row-bg-overlay)',
              },

              '& .MuiDataGrid-row': {
                background: 'var(--devices-grid-row-bg-overlay)',
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                background: 'var(--devices-grid-row-bg-overlay)',
              },
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
                background: 'var(--devices-grid-row-bg-overlay)',
              },
              '& .MuiDataGrid-row.disabled-device-row': {
                opacity: 0.55,
              },
              '& .MuiDataGrid-row.disabled-device-row .MuiDataGrid-cell': {
                color: 'var(--text-color-light)',
              },
              '& .MuiDataGrid-row.disabled-device-row:hover': {
                cursor: 'not-allowed',
                background: 'var(--devices-grid-row-bg-overlay)',
              },

              '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover':
                {
                  background: 'var(--devices-grid-row-bg-overlay) !important',
                },
              '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell': {
                color: 'var(--devices-grid-selected-text)',
              },

              '& .MuiCheckbox-root, & .MuiDataGrid-sortIcon, & .MuiSvgIcon-root': {
                color: 'var(--devices-grid-icon)',
              },

              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid var(--devices-grid-footer-border)',
                background: 'var(--devices-grid-footer-bg)',
                fontSize: 'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                color: 'var(--devices-grid-pagination-text)',
              },
              '& .MuiTablePagination-root': {
                color: 'var(--devices-grid-pagination-text)',
              },

              '& .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-filler': {
                backgroundColor: 'var(--devices-grid-footer-bg)',
              },

              '& .MuiDataGrid-toolbarContainer': {
                backgroundColor: 'transparent',
                fontSize: 'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                color: 'var(--root-color)',
              },

              '& .MuiDataGrid-actionsCell .MuiIconButton-root': {
                color: 'var(--root-color)',
              },

              '& .MuiPopover-root .MuiMenu-list .MuiMenuItem-root': {
                minHeight: 'auto !important',
                alignItems: 'center',
                fontSize: 'calc(var(--ui-font-size) * var(--font-size-multiplier))',
                paddingTop: '6px',
                paddingBottom: '6px',
              },

              '& .MuiDataGrid-scrollbar': {
                display: 'none',
              },

              '& .MuiDataGrid-virtualScroller': {
                background: 'var(--devices-grid-row-bg-overlay)',
                overflow: 'auto',
              },
            }}
            slots={{
              toolbar: showToolbar ? GridToolbar : null,
              pagination: CustomPagination,
              moreActionsIcon: MoreVertIcon,
              baseCheckbox: DataGridAccessibleCheckbox,
            }}
            localeText={localeText}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            slotProps={{
              toolbar: {
                model,
                csvOptions: {
                  utf8WithBom: true,
                  fileName: sanitizedTableKey || 'export',
                },
              },
              baseCheckbox: {
                fieldNamePrefix: `${sanitizedTableKey}-selection`,
              },
            }}
            onRowClick={handleRowClick}
            getRowClassName={getRowClassName}
            sortModel={effectiveSortModel}
            columnVisibilityModel={gridSettings.columnVisibilityModel}
            filterModel={gridSettings.filterModel}
            density={gridSettings.density}
            onSortModelChange={handleSortModelChange}
            onColumnVisibilityModelChange={(newModel) =>
              setGridSettings((prev) => ({
                ...prev,
                columnVisibilityModel: newModel,
              }))
            }
            onColumnWidthChange={(params) => {
              const field = params?.colDef?.field;
              const width = params?.width;
              if (!field || typeof width !== 'number') return;
              setGridSettings((prev) => ({
                ...prev,
                columnWidthModel: {
                  ...(prev.columnWidthModel || {}),
                  [field]: width,
                },
              }));
            }}
            onFilterModelChange={handleGridFilterModelChange}
            onDensityChange={(newDensity) =>
              setGridSettings((prev) => ({
                ...prev,
                density: newDensity,
              }))
            }
            paginationMode={isServer ? 'server' : 'client'}
            sortingMode={isServer ? 'server' : 'client'}
            filterMode={isServer ? 'server' : 'client'}
            paginationModel={
              isServer ? { page: serverPage - 1, pageSize: serverPageSize } : undefined
            }
            onPaginationModelChange={isServer ? handlePaginationModelChange : undefined}
            {...(isServer ? { rowCount: serverRowCount } : {})}
          />
        </div>
      </div>
    </>
  );
}
